import {
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Snackbar,
	Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import useFileUpload from '../../../../hooks/useFileUpload';

/**
 * FileUploader component for handling file uploads with drag-and-drop support
 * @param {string} tenantId - The tenant ID
 * @param {string} objectId - The object ID (e.g., dealId)
 * @param {Function} onFilesAdded - Callback when files are successfully added
 * @param {string} buttonIcon - Optional custom icon component
 * @param {string} buttonTooltip - Optional tooltip text
 * @param {Object} buttonProps - Optional additional props for the trigger button
 */
const FileUploader = ({
	tenantId,
	objectId,
	onFilesAdded,
	buttonIcon = <AddIcon />,
	buttonTooltip = 'Upload files',
	buttonProps = {},
}) => {
	const {
		openModal,
		success,
		error,
		isDragActive,
		handleOpenModal,
		handleCloseModal,
		handleCloseSnackbar,
		getRootProps,
		getInputProps,
		allowedExtensions,
	} = useFileUpload(tenantId, objectId, onFilesAdded);

	return (
		<>
			{/* Upload trigger button */}
			<IconButton
				color='primary'
				onClick={handleOpenModal}
				title={buttonTooltip}
				{...buttonProps}
			>
				{buttonIcon}
			</IconButton>

			{/* Upload modal with dropzone */}
			<Dialog
				open={openModal}
				onClose={handleCloseModal}
				PaperProps={{ elevation: 3 }}
			>
				<DialogTitle>Upload Source File</DialogTitle>
				<DialogContent
					{...getRootProps()}
					sx={{
						textAlign: 'center',
						border: '2px dashed #ccc',
						borderRadius: '4px',
						py: 4,
						cursor: 'pointer',
						opacity: isDragActive ? 1 : 0.5,
						...(isDragActive && {
							backgroundColor: 'primary.light',
							borderColor: 'primary.main',
						}),
					}}
				>
					<input {...getInputProps()} />
					<CloudUploadIcon fontSize='large' color='primary' />
					<Typography variant='h6'>
						{isDragActive
							? 'Drop files here...'
							: 'Drag files here or click to select'}
					</Typography>
					<Typography variant='caption'>Maximum size is 2GB</Typography>
					<Typography variant='caption' display='block' sx={{ mt: 1 }}>
						Allowed file types: {allowedExtensions.join(', ')}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal} color='primary'>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>

			{/* Success notification */}
			<Snackbar
				open={success}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity='success'
					sx={{ width: '100%' }}
				>
					File uploaded successfully!
				</Alert>
			</Snackbar>

			{/* Error notification */}
			<Snackbar
				open={!!error}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity='error'
					sx={{ width: '100%' }}
				>
					{error}
				</Alert>
			</Snackbar>
		</>
	);
};

export default FileUploader;
